<template>
  <div class="task-calendar">
    <FullCalendar :options="calendarOptions" />
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import ruLocale from '@fullcalendar/core/locales/ru'
import {onBeforeUnmount, watch} from "@vue/runtime-core";
import {useStore} from "vuex";

export default {
  name: 'TaskCalendar',
  components: {
    FullCalendar,
  },
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['task-click', 'day-click', 'month-change'],
  setup(props, { emit }) {
    const lastEmittedRangeKey = ref(null)

    const colorMap = {
      'Красный': '#9c0130',
      'Жёлтый': '#ff9e02',
      'Желтый': '#ff9e02',
      'Синий': '#100f76',
      'Зелёный': '#3d9123',
      'Зеленый': '#3d9123',
    }

    const getTaskTitle = (task) => {
      return task?.task?.name || task?.name || task?.title || 'Без названия'
    }

    const getTaskColor = (task) => {
      if (!task?.color?.name) {
        return '#B8BEC9'
      }

      return colorMap[task.color.name] || '#B8BEC9'
    }

    const getIntervalStartMinutes = (interval) => {
      const [start] = interval.split('-')
      const [hours, minutes] = start.split(':').map(Number)

      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return 9999
      }

      return hours * 60 + minutes
    }

    const mapTasksToEvents = (tasks) => {
      return (tasks || [])
          .filter(task => task?.date)
          .map((task) => ({
            id: String(task.id),
            title: getTaskTitle(task),
            start: task.date,
            allDay: true,
            extendedProps: {
              taskId: task.id,
              plannedTaskId: task.planned_task_id,
              occurredTaskId: task.occurred_task_id,
              source: task.source,
              happened: !!task.already_happened,
              isFuture: !!task.is_future,
              isTodayExpected: !!task.is_today_expected,
              feedbackRequired: !!task.feedback_required,
              interval: task.interval || null,
              color: getTaskColor(task),
              rawTask: task,
            },
          }))
    }

    const calendarEvents = computed(() => mapTasksToEvents(props.tasks))

    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    }

    const emitMonthRange = (info) => {
      const currentStart = new Date(info.view.currentStart)
      const currentEnd = new Date(info.view.currentEnd)

      currentEnd.setDate(currentEnd.getDate() - 1)

      const start = formatDate(currentStart)
      const end = formatDate(currentEnd)
      const rangeKey = `${start}_${end}_${info.view.type}`

      if (lastEmittedRangeKey.value === rangeKey) {
        return
      }

      lastEmittedRangeKey.value = rangeKey

      emit('month-change', {
        start,
        end,
        viewType: info.view.type,
      })
    }

    const handleDateClick = (info) => {
      emit('day-click', {
        date: info.dateStr,
        jsEvent: info.jsEvent,
        viewType: info.view.type,
      })
    }

    const handleEventClick = (info) => {
      emit('task-click', {
        id: info.event.extendedProps.taskId,
        task: info.event.extendedProps.rawTask,
        event: info.event,
        jsEvent: info.jsEvent,
      })
    }

    const renderEventContent = (arg) => {
      const happened = !!arg.event.extendedProps.happened
      const color = arg.event.extendedProps.color || '#B8BEC9'
      const interval = arg.event.extendedProps.interval

      const wrapper = document.createElement('div')
      wrapper.className = 'task-event-wrap'

      const pill = document.createElement('div')
      pill.className = happened
          ? 'task-event-pill is-happened'
          : 'task-event-pill is-pending'

      if (happened) {
        pill.style.backgroundColor = `${color}22`
        pill.style.borderColor = `${color}55`
      }

      const dot = document.createElement('span')
      dot.className = 'task-event-dot'
      dot.style.backgroundColor = color

      const textWrap = document.createElement('div')
      textWrap.className = 'task-event-text-wrap'

      const label = document.createElement('span')
      label.className = 'task-event-label'
      label.textContent = arg.event.title

      textWrap.appendChild(label)

      if (interval) {
        const time = document.createElement('span')
        time.className = 'task-event-time'
        time.textContent = interval
        textWrap.appendChild(time)
      }

      pill.appendChild(dot)
      pill.appendChild(textWrap)
      wrapper.appendChild(pill)

      return { domNodes: [wrapper] }
    }

    const calendarOptions = computed(() => ({
      plugins: [dayGridPlugin, interactionPlugin],
      locales: [ruLocale],
      locale: 'ru',
      initialView: 'dayGridMonth',
      height: 'auto',
      fixedWeekCount: false,
      editable: false,
      selectable: false,
      dayMaxEvents: 3,
      events: calendarEvents.value,
      eventOrder: (a, b) => {
        const aMinutes = getIntervalStartMinutes(a.extendedProps.interval)
        const bMinutes = getIntervalStartMinutes(b.extendedProps.interval)

        if (aMinutes !== bMinutes) {
          return aMinutes - bMinutes
        }

        return a.title.localeCompare(b.title, 'ru')
      },

      headerToolbar: {
        left: 'prev',
        center: 'title',
        right: 'next',
      },

      datesSet(info) {
        emitMonthRange(info)
      },

      dateClick(info) {
        handleDateClick(info)
      },

      eventClick(info) {
        handleEventClick(info)
      },

      eventContent(arg) {
        return renderEventContent(arg)
      },
    }));

    return {
      calendarOptions,
    }
  },
}
</script>

<style>
.task-calendar {
  background: #ffffff;
  border: 1px solid #e5e8ef;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.task-calendar .fc .fc-toolbar {
  padding: 16px 18px;
  margin-bottom: 0;
  border-bottom: 1px solid #e9edf5;
  background: #fff;
}

.task-calendar .fc .fc-toolbar-title {
  font-size: 20px;
  line-height: 1.2;
  font-weight: 700;
  color: #1f2937;
}

.task-calendar .fc .fc-button {
  border: 1px solid #d9dfeb;
  background: #fff;
  color: #344054;
  border-radius: 10px;
  box-shadow: none;
  padding: 8px 12px;
}

.task-calendar .fc .fc-button:hover,
.task-calendar .fc .fc-button:focus {
  background: #f7f9fc;
  border-color: #cfd6e4;
  color: #1f2937;
  box-shadow: none;
}

.task-calendar .fc .fc-button-primary:not(:disabled).fc-button-active {
  background: #f7f9fc;
  border-color: #cfd6e4;
  color: #1f2937;
}

.task-calendar .fc .fc-scrollgrid {
  border: none;
}

.task-calendar .fc-theme-standard td,
.task-calendar .fc-theme-standard th {
  border-color: #e9edf5;
}

.task-calendar .fc .fc-col-header-cell {
  background: #f8fafc;
  padding: 10px 0;
}

.task-calendar .fc .fc-col-header-cell-cushion {
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  text-decoration: none;
  padding: 6px 8px;
}

.task-calendar .fc .fc-daygrid-day-frame {
  min-height: 126px;
  background: #fff;
}

.task-calendar .fc .fc-daygrid-day-top {
  justify-content: flex-start;
  padding: 8px 8px 0;
}

.task-calendar .fc .fc-daygrid-day-number {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #344054;
  font-size: 13px;
  text-decoration: none;
}

.task-calendar .fc .fc-day-today {
  background: #fcfcfd;
}

.task-calendar .fc .fc-day-today .fc-daygrid-day-number {
  background: #d4ecdf;
  color: #2cc185;
  font-weight: 700;
}

.task-calendar .fc .fc-daygrid-day-events {
  margin-top: 6px;
  padding: 0 6px 8px;
}

.task-calendar .fc .fc-daygrid-more-link {
  color: #667085;
  font-size: 12px;
  text-decoration: none;
  margin-top: 4px;
}

.task-event-wrap {
  width: 100%;
}

.task-event-pill {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 28px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  overflow: hidden;
  box-sizing: border-box;
}

.task-event-pill.is-pending {
  background: #f2f4f7;
  border-color: #eaecf0;
}

.task-event-pill.is-happened {
  background: #eef2ff;
}

.task-event-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 auto;
  margin-top: 4px;
}

.task-event-text-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.task-event-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
  color: #344054;
}

.task-event-time {
  font-size: 10px;
  line-height: 1.2;
  color: #667085;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-calendar .fc-h-event {
  background: transparent;
  border: none;
}

.task-calendar .fc-daygrid-event {
  margin-top: 4px;
  cursor: pointer;
}

.task-calendar .fc-daygrid-day {
  cursor: pointer;
}

@media (max-width: 900px) {
  .task-calendar .fc .fc-toolbar {
    padding: 12px;
  }

  .task-calendar .fc .fc-toolbar-title {
    font-size: 17px;
  }

  .task-calendar .fc .fc-daygrid-day-frame {
    min-height: 100px;
  }

  .task-event-pill {
    padding: 4px 8px;
  }

  .task-event-label {
    font-size: 11px;
  }
}

.task-calendar .fc .fc-popover {
  border: 1px solid #d9dfeb;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  overflow: hidden;
}

.task-calendar .fc .fc-popover-title {
  font-size: 14px;
  font-weight: 700;
  color: #344054;
}

.task-calendar .fc .fc-popover-close {
  font-size: 18px;
  color: #667085;
}
.task-calendar .fc .fc-popover {
  border: 1px solid #d9dfeb;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  overflow: hidden;
}

.task-calendar .fc .fc-popover-header {
  background: #f8fafc;
  padding: 10px 14px;
  border-bottom: 1px solid #e9edf5;
}

.task-calendar .fc .fc-popover-body {
  max-height: min(260px, 60vh);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 8px 16px;
  box-sizing: border-box;
}

.task-calendar .fc .fc-popover {
  overflow: hidden;
}
</style>